import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useSnackbar } from 'notistack';
import LockIcon from '@mui/icons-material/Lock';

const VIEW = 'view';

// 증빙유형
const EVIDENCE_TYPES = {
  TAX_INVOICE: '세금계산서',
  ZERO_TAX_INVOICE: '영세율세금계산서',
  INVOICE: '계산서',
  CARD: '카드',
  CERTIFICATION: '면장',
  FOREIGN_INVOICE: '인보이스',
  FOREIGN_PURCHASE: '외환매입증명',
  OTHER: '기타',
};

// VAT가 0원인 증빙유형
const ZERO_VAT_TYPES = [
  EVIDENCE_TYPES.ZERO_TAX_INVOICE,
  EVIDENCE_TYPES.INVOICE,
  EVIDENCE_TYPES.CARD,
  EVIDENCE_TYPES.OTHER,
];

// 외환 관련 추가 필드가 필요한 증빙유형
const FOREIGN_CURRENCY_TYPES = [
  EVIDENCE_TYPES.CERTIFICATION,
  EVIDENCE_TYPES.FOREIGN_INVOICE,
  EVIDENCE_TYPES.FOREIGN_PURCHASE,
];

const FOREIGN_KEYS = Object.keys(EVIDENCE_TYPES)
  .filter((key) => FOREIGN_CURRENCY_TYPES.includes(EVIDENCE_TYPES[key]));

const CURRENCY_TYPES = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'JPY', label: 'JPY' },
  { value: 'CNY', label: 'CNY' },
  { value: 'GBP', label: 'GBP' },
];

// 분할 개수 상한. 없으면 실수로 1000을 넣어 브라우저가 멈춘다.
const MAX_INSTALLMENT = 60;

const TRJ_NO_KEY = 'NO_KEY';

/**
 * 금액 표시. 공용 getNumberComma 는 0을 '-' 로 돌려준다(0이 falsy).
 * 입력칸에 0 대신 '-' 가 뜨면 지운 줄 알게 되고, 합계 0도 값이 없는 것처럼 보인다.
 * 공용 유틸은 다른 화면이 그 동작에 기대고 있을 수 있어 여기서만 따로 쓴다.
 */
const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ko-KR');
};

const toNumber = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const calcVat = (evidenceType, invAmt) => {
  if (ZERO_VAT_TYPES.includes(EVIDENCE_TYPES[evidenceType])) return 0;
  if (evidenceType === 'TAX_INVOICE') return Math.round(invAmt * 0.1);
  return null; // 그 외는 사용자 입력값을 유지한다
};

/**
 * 이 회차를 왜 못 고치는지. 고쳐도 되면 null.
 *
 * 발행 확정분은 이미 확정된 사실이라 못 바꾼다.
 * 발행 결재가 걸린 회차는 아직 확정 전이지만 결재 문서가 이 행을 붙잡고 있어서,
 * 지우면 결재 취소가 엉뚱한 행을 건드리게 된다.
 * TRJ_UUID 는 결재 기능을 쓸 때만 채워지므로 비어 있는 값(NULL 포함)은 미연결로 본다.
 */
export const lockReasonOf = (row) => {
  if (!row) return null;
  if (row.lockReason) return row.lockReason;
  if (row.reqCompYn === 'Y') return '발행 확정';
  if (row.trjUuid && row.trjUuid !== TRJ_NO_KEY) return '발행 결재 진행중';
  return null;
};

// 금액 입력. type="number" 는 천단위 콤마가 안 보여서 자릿수를 눈으로 세야 한다.
const AmountInput = React.memo(function AmountInput({ value, onChange, disabled }) {
  return (
    <TextField
      value={fmt(toNumber(value))}
      onChange={(e) => onChange(String(e.target.value).replace(/[^0-9]/g, ''))}
      variant="standard"
      size="small"
      fullWidth
      disabled={disabled}
      inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
    />
  );
});

const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={(v) => onChange(v && v.isValid() ? v.format('YYYY-MM-DD') : null)}
        format="YYYY-MM-DD"
        disabled={disabled}
        // v6 에서 renderInput 이 없어졌다. 그대로 두면 variant/size 가 통째로 무시돼
        // 날짜 칸만 커진다.
        slotProps={{
          textField: { variant: 'standard', size: 'small', fullWidth: true },
        }}
        sx={{ minWidth: 130 }}
      />
    </LocalizationProvider>
  );
});

export default function InvoiceSplitDialog({
  open,
  onClose,
  onSave,
  billrows,
  billUuid,
  type,
  mode,
  partnerName,
  installmentCount,
  totalAmount,
  defaultPlanDt, // 계약완료일 (첫 회차 기준일)
}) {
  const isView = mode === VIEW;
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [splitCount, setSplitCount] = useState(installmentCount || 1);
  // 사용자가 직접 고친 게 있는지. 균등분할로 날리기 전에 확인을 받기 위해 본다.
  const [dirty, setDirty] = useState(false);
  const initializedRef = useRef(false);

  const totalAmt = toNumber(totalAmount);

  // ── 잠금/가용 분리 ────────────────────────────────────────────────
  const lockedRows = useMemo(() => rows.filter((r) => lockReasonOf(r)), [rows]);
  const openRows = useMemo(() => rows.filter((r) => !lockReasonOf(r)), [rows]);

  const lockedAmt = useMemo(
    () => lockedRows.reduce((acc, r) => acc + toNumber(r.invAmt), 0),
    [lockedRows],
  );
  // 재분배 가능액. 확정된 금액은 손댈 수 없으므로 총액에서 뺀다.
  const availableAmt = totalAmt - lockedAmt;
  const inputAmt = useMemo(
    () => openRows.reduce((acc, r) => acc + toNumber(r.invAmt), 0),
    [openRows],
  );
  const diffAmt = availableAmt - inputAmt;
  const balanced = diffAmt === 0;

  const sumAmt = useMemo(() => rows.reduce((a, r) => a + toNumber(r.invAmt), 0), [rows]);
  const sumVat = useMemo(() => rows.reduce((a, r) => a + toNumber(r.invVat), 0), [rows]);
  const sumTot = useMemo(() => rows.reduce((a, r) => a + toNumber(r.invTot), 0), [rows]);

  // 외환 컬럼은 실제로 쓰는 행이 있을 때만 보여준다. 항상 띄우면 표가 불필요하게 넓다.
  const showForeign = useMemo(
    () => rows.some((r) => FOREIGN_KEYS.includes(r.evidenceType)),
    [rows],
  );

  /**
   * 남은 금액을 n개로 균등 분할한다.
   * 나누어떨어지지 않는 잔돈은 **마지막 회차**에 몰아준다.
   * 첫 회차에 붙이면 첫 줄 금액만 튀어 보여서 입력 실수처럼 읽힌다.
   */
  const buildSplitRows = useCallback((count, baseIndex) => {
    const n = Math.max(1, count);
    const base = Math.floor(availableAmt / n);
    const remainder = availableAmt - base * n;

    // 기준일이 과거면 오늘로 당긴다. 진행 중인 계약에서 열면 계약완료일이 이미 지나 있다.
    let baseDate = defaultPlanDt ? dayjs(defaultPlanDt) : dayjs();
    if (!baseDate.isValid() || baseDate.isBefore(dayjs(), 'day')) baseDate = dayjs();

    return Array.from({ length: n }).map((_, i) => {
      const invAmt = base + (i === n - 1 ? remainder : 0);
      const date = baseDate.add(i, 'month').endOf('month').format('YYYY-MM-DD');
      const evidenceType = 'TAX_INVOICE';
      const invVat = calcVat(evidenceType, invAmt) ?? 0;
      return {
        id: `new-${baseIndex + i + 1}`,
        billUuid,
        reqPlanDt: date,
        procPlanDt: date,
        rsltCompDt: date,
        // 발행일(reqCompDt)은 실제로 발행할 때 찍히는 값이다.
        // 계획 단계에서 미리 채우면, 발행도 안 했는데 화면에 발행일이 보여
        // "상태는 작성중인데 발행일은 있다" 는 모순된 화면이 된다.
        reqCompDt: null,
        invAmt,
        invVat,
        invTot: invAmt + invVat,
        invTxt: partnerName,
        reqCompYn: 'N',
        trjUuid: TRJ_NO_KEY,
        evidenceType,
        currencyType: null,
        foreignAmount: null,
      };
    });
  }, [availableAmt, defaultPlanDt, billUuid, partnerName]);

  // ── 초기화 ────────────────────────────────────────────────────────
  // 저장돼 있던 계획을 그대로 싣는다.
  // 예전에는 열 때마다 계획을 새로 만들어서, 이미 잡아둔 발행예정일·발행내역이 사라졌다.
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    setDirty(false);

    if (billrows && billrows.length > 0) {
      const loaded = billrows.map((row, idx) => {
        const invAmt = toNumber(row.invAmt);
        const evidenceType = row.evidenceType || 'TAX_INVOICE';
        const autoVat = calcVat(evidenceType, invAmt);
        const invVat = autoVat === null ? toNumber(row.invVat) : autoVat;
        return {
          ...row,
          id: row.id ?? row.invUuid ?? `row-${idx}`,
          evidenceType,
          currencyType: row.currencyType || null,
          foreignAmount: row.foreignAmount ?? null,
          invAmt,
          invVat,
          invTot: invAmt + invVat,
        };
      });
      setRows(loaded);
      setSplitCount(loaded.length);
    } else {
      const generated = buildSplitRows(installmentCount || 1, 0);
      setRows(generated);
      setSplitCount(generated.length);
    }
  }, [open, billrows, installmentCount, buildSplitRows]);

  // ── 셀 변경 ───────────────────────────────────────────────────────
  const handleCellChange = useCallback((id, field, value) => {
    setDirty(true);
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;

        const next = { ...r, [field]: value };

        if (field === 'evidenceType') {
          const invAmt = toNumber(next.invAmt);
          const autoVat = calcVat(value, invAmt);
          if (autoVat !== null) next.invVat = autoVat;
          if (!FOREIGN_KEYS.includes(value)) {
            next.currencyType = null;
            next.foreignAmount = null;
          }
        }

        if (field === 'invAmt') {
          const invAmt = toNumber(value);
          next.invAmt = invAmt;
          const autoVat = calcVat(next.evidenceType, invAmt);
          if (autoVat !== null) next.invVat = autoVat;
        }

        if (field === 'invVat') next.invVat = toNumber(value);

        next.invTot = toNumber(next.invAmt) + toNumber(next.invVat);
        return next;
      }),
    );
  }, []);

  // ── 균등분할 ──────────────────────────────────────────────────────
  // 개수를 바꾸는 즉시 자동으로 다시 나누지 않는다.
  // 예전에는 숫자만 건드려도 손으로 맞춰둔 날짜·금액이 경고 없이 전부 초기화됐다.
  const handleApplySplit = useCallback(() => {
    const count = Number(splitCount);
    if (!Number.isInteger(count) || count < 1) {
      enqueueSnackbar('분할 개수를 1 이상으로 입력해 주세요', { variant: 'warning' });
      return;
    }
    if (count > MAX_INSTALLMENT) {
      enqueueSnackbar(`분할 개수는 최대 ${MAX_INSTALLMENT}회까지 가능합니다`, { variant: 'warning' });
      return;
    }
    if (availableAmt <= 0) {
      enqueueSnackbar('재분배할 금액이 없습니다', { variant: 'warning' });
      return;
    }
    if (dirty && openRows.length > 0) {
      const ok = window.confirm(
        '직접 입력한 내용이 초기화됩니다.\n남은 금액을 다시 균등하게 나눌까요?',
      );
      if (!ok) return;
    }

    setRows([...lockedRows, ...buildSplitRows(count, lockedRows.length)]);
    setDirty(false);
  }, [splitCount, availableAmt, dirty, openRows.length, lockedRows, buildSplitRows, enqueueSnackbar]);

  // ── 저장 ──────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const invalid = rows.find((row) => {
      if (!row.evidenceType) return true;
      if (FOREIGN_KEYS.includes(row.evidenceType)) {
        if (!row.currencyType || row.foreignAmount === null || row.foreignAmount === '') return true;
      }
      if (!row.reqPlanDt) return true;
      return false;
    });
    if (invalid) {
      enqueueSnackbar('발행 예정일과 증빙 정보를 모두 입력해 주세요', { variant: 'error' });
      return;
    }

    if (!balanced) {
      enqueueSnackbar('금액이 일치하지 않습니다', { variant: 'error' });
      return;
    }

    if (rows.some((r) => toNumber(r.invAmt) <= 0)) {
      enqueueSnackbar('회차 금액은 0보다 커야 합니다', { variant: 'error' });
      return;
    }

    onSave(rows);
  }, [rows, balanced, onSave, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    initializedRef.current = false;
    onClose();
  }, [onClose]);

  const handleCountInput = useCallback((e) => {
    const raw = String(e.target.value).replace(/[^0-9]/g, '');
    if (raw === '') {
      setSplitCount('');
      return;
    }
    setSplitCount(Math.min(Number(raw), MAX_INSTALLMENT));
  }, []);

  // ── 렌더 ──────────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const cols = [
      { field: 'reqPlanDt', header: '발행 예정일', type: 'date', width: 150 },
      { field: 'rsltCompDt', header: '실적 기준일', type: 'date', width: 150 },
      { field: 'procPlanDt', header: '수금(지급)예정일', type: 'date', width: 150 },
      { field: 'invTxt', header: '발행 내역', type: 'string' },
      {
        field: 'evidenceType',
        header: '증빙',
        type: 'select',
        width: 130,
        options: Object.entries(EVIDENCE_TYPES).map(([value, label]) => ({ value, label })),
      },
    ];
    if (showForeign) {
      cols.push(
        { field: 'currencyType', header: '환종류', type: 'select', width: 100, options: CURRENCY_TYPES, foreignOnly: true },
        { field: 'foreignAmount', header: '외환 금액', type: 'amount', width: 120, foreignOnly: true },
      );
    }
    cols.push(
      { field: 'invAmt', header: '원화금액', type: 'amount', width: 140, align: 'right' },
      { field: 'invVat', header: 'VAT', type: 'amount', width: 120, align: 'right' },
      { field: 'invTot', header: '합계', type: 'total', width: 140, align: 'right' },
    );
    return cols;
  }, [showForeign]);

  const renderCell = (row, col) => {
    const value = row[col.field];
    const lockReason = lockReasonOf(row);
    const disabled = isView || Boolean(lockReason);

    if (col.foreignOnly && !FOREIGN_KEYS.includes(row.evidenceType)) {
      return <Typography variant="body2" color="text.disabled">-</Typography>;
    }

    if (col.type === 'total') {
      return (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {fmt(toNumber(value))}
        </Typography>
      );
    }

    if (disabled) {
      if (col.type === 'amount') {
        return <Typography variant="body2">{fmt(toNumber(value))}</Typography>;
      }
      if (col.type === 'select') {
        const opt = (col.options || []).find((o) => o.value === value);
        return <Typography variant="body2">{opt ? opt.label : '-'}</Typography>;
      }
      return <Typography variant="body2">{value || '-'}</Typography>;
    }

    if (col.type === 'date') {
      return (
        <DateInput
          value={value}
          onChange={(v) => handleCellChange(row.id, col.field, v)}
        />
      );
    }

    if (col.type === 'amount') {
      // VAT가 자동 계산되는 증빙유형은 직접 못 고친다.
      const vatLocked = col.field === 'invVat' && calcVat(row.evidenceType, 0) !== null;
      return (
        <AmountInput
          value={value}
          onChange={(v) => handleCellChange(row.id, col.field, v)}
          disabled={vatLocked}
        />
      );
    }

    if (col.type === 'select') {
      return (
        <FormControl variant="standard" fullWidth>
          <Select
            value={value || ''}
            onChange={(e) => handleCellChange(row.id, col.field, e.target.value)}
          >
            {(col.options || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        value={value || ''}
        onChange={(e) => handleCellChange(row.id, col.field, e.target.value)}
        variant="standard"
        size="small"
        fullWidth
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        계산서 분할 상세
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
          {type} · {partnerName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* 요약 — 맞는지 틀린지를 표 위에서 바로 알 수 있어야 한다.
            예전에는 저장을 눌러야 금액 불일치를 알려줬다. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 3,
            px: 1.5,
            py: 1.5,
            mb: 2,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        >
          <SummaryItem label="총 발행 금액" value={totalAmt} />
          {lockedRows.length > 0 && (
            <SummaryItem label={`확정 (${lockedRows.length}회차)`} value={lockedAmt} />
          )}
          <SummaryItem label="분배 가능액" value={availableAmt} strong />
          <SummaryItem label="입력 합계" value={inputAmt} />
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={balanced ? '금액 일치' : `차액 ${fmt(diffAmt)}`}
            color={balanced ? 'success' : 'error'}
            variant={balanced ? 'outlined' : 'filled'}
            sx={{ fontWeight: 700 }}
          />
        </Box>

        {!isView && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>분할 개수</Typography>
            <TextField
              size="small"
              value={splitCount}
              onChange={handleCountInput}
              inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' } }}
              sx={{ width: 80 }}
            />
            <Button variant="outlined" size="small" onClick={handleApplySplit}>
              균등분할
            </Button>
            <Typography variant="caption" color="text.secondary">
              누르면 남은 금액을 다시 나눕니다. 개수만 바꾸는 것으로는 표가 변하지 않습니다.
            </Typography>
          </Box>
        )}

        {lockedRows.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            잠긴 {lockedRows.length}개 회차는 수정할 수 없습니다. 나머지 금액만 다시 나눌 수 있습니다.
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, overflowX: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 56 }}>회차</TableCell>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align || 'left'}
                    sx={{ fontWeight: 700, minWidth: col.width }}
                  >
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const lockReason = lockReasonOf(row);
                return (
                  <TableRow
                    key={row.id}
                    sx={lockReason ? { backgroundColor: 'action.hover' } : undefined}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{idx + 1}</Typography>
                        {lockReason && (
                          // 회색으로만 두면 왜 못 고치는지 알 수 없다.
                          <Tooltip title={lockReason}>
                            <LockIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                          </Tooltip>
                        )}
                      </Box>
                      {lockReason && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {lockReason}
                        </Typography>
                      )}
                    </TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.field} align={col.align || 'left'}>
                        {renderCell(row, col)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                    회차가 없습니다. 분할 개수를 입력하고 균등분할을 눌러 주세요.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="flex-end" gap={4} mt={1.5} px={1}>
          <Typography variant="body2"><strong>합계 금액</strong> {fmt(sumAmt)}</Typography>
          <Typography variant="body2"><strong>합계 VAT</strong> {fmt(sumVat)}</Typography>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
            총 합계 {fmt(sumTot)}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isView && !balanced && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
            분배 가능액과 {fmt(Math.abs(diffAmt))} 차이가 납니다
          </Typography>
        )}
        <Button onClick={handleClose}>취소</Button>
        {!isView && (
          <Button variant="contained" onClick={handleSave} disabled={!balanced || rows.length === 0}>
            확인
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function SummaryItem({ label, value, strong }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography
        variant={strong ? 'subtitle1' : 'body2'}
        sx={{ fontWeight: strong ? 700 : 500, color: strong ? 'primary.main' : 'text.primary' }}
      >
        {fmt(value)}
      </Typography>
    </Box>
  );
}
